if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this.classesToRemoveOnLoad = 'drawer--loading';
        this.drawerBody = this.querySelector(this.selector);
      }

      get selector() {
        return '.quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      prepareToShow() {
        super.prepareToShow();
        this.__qvActive = true;
        this.quickview();
      }

      handleAfterShow() {
        super.handleAfterShow();
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl }
          })
        );
      }

      cancelQuickView() {
        this.__qvActive = false;
        this.__qvRequest?.abort();
        this.__qvRequest = null;
        this.__qvLoading = false;
      }

      prepareToHide() {
        this.cancelQuickView();
        super.prepareToHide();
      }

      disconnectedCallback() {
        // DrawerComponent.show() re-parents this element onto <body>, and a move fires
        // disconnectedCallback immediately followed by connectedCallback. Cancelling here
        // unconditionally aborted the section request that prepareToShow() had started a
        // moment earlier, so the drawer opened and spun forever. Only a real removal, one
        // that is still disconnected after the current task, should cancel the load.
        queueMicrotask(() => {
          if (!this.isConnected) this.cancelQuickView();
        });
        super.disconnectedCallback();
      }

      handleAfterHide() {
        if (this.open) return; // an old hide transition must not clear a reopen
        this.cancelQuickView();
        super.handleAfterHide();
        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.add(this.classesToRemoveOnLoad);
      }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }

          // Cache for better performance.
          FoxTheme.QuickViewSectionId = sectionId;
        }

        return sectionId;
      }

      quickview() {
        // Guard: one load per open. Some browsers fire the open twice, and a duplicate late load
        // re-renders the default variant, wiping any option the user picked before it resolved.
        if (this.__qvLoading) return;
        this.__qvLoading = true;
        const request = new AbortController();
        this.__qvRequest = request;
        document.querySelectorAll('quick-view-modal').forEach((modal) => {
          if (modal !== this) {
            modal.hide();
            modal.cancelQuickView();
          }
        });

        const drawerContent = this.querySelector(this.selector);
        const sectionId = this.getProductQuickViewSectionId();
        const sectionUrl = `${this.dataset.productUrl.split('?')[0]}?section_id=${sectionId}`;

        const productUrl = this.dataset.productUrl;
        fetch(sectionUrl, { signal: request.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Quick view failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            if (this.__qvRequest !== request || !this.__qvActive || !this.open) return;
            const productElement = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.selector);

            if (!productElement) throw new Error("Quick view content is unavailable");
            this.setInnerHTML(drawerContent, productElement.innerHTML);

            FoxTheme.a11y.trapFocus(this, this.focusElement);
            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            document.dispatchEvent(
              new CustomEvent('quick-view:loaded', {
                detail: { productUrl }
              })
            );
          })
          .catch((e) => {
            if (e.name !== "AbortError" && this.__qvRequest === request) console.error(e);
          })
          .finally(() => {
            if (this.__qvRequest === request) {
              this.__qvLoading = false;
              this.__qvRequest = null;
            }
          });
      }

      setInnerHTML(element, innerHTML) {
        // Remove old variant-selects before setting new innerHTML
        const oldVariantSelects = element.querySelectorAll('variant-selects');
        oldVariantSelects.forEach((vs) => vs.remove());

        element.innerHTML = innerHTML;

        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        this.classList.remove(this.classesToRemoveOnLoad);
      }


    }
  );
}
